import { HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { OAuthService } from 'angular-oauth2-oidc';

/**
 * 0.62.0.4 — OIDC-Bearer-Interceptor.
 *
 * Hängt `Authorization: Bearer <access_token>` an alle Backend-API-Calls
 * an, sofern ein gültiger Token vorliegt.
 *
 * Hintergrund (PO-Befund 2026-05-05):
 * In 0.62.0.2 wurde der `fakeAuthInterceptor` entfernt (Persona-Switcher-
 * Removal Step 2/3) und dabei dokumentiert, dass „der OIDC-Bearer-
 * Interceptor wird in `FatipOidcAuthService.configure()` registriert" —
 * der Code dazu wurde aber nie geschrieben. `provideOAuthClient` hat
 * `sendAccessToken: false`, und `configure()` setzt keine
 * `resourceServer`-Konfig. Folge: jeder `/api/...`-Call ging anonym ans
 * Backend → User verloren PLATFORM_ADMIN-Rechte (Visibility-Resolver
 * sieht nur die 4 Default-Module). Bug-Lebensdauer: 0.62.0.2 (Commit
 * vom 2026-05-04) bis 0.62.0.4 (Commit vom 2026-05-05).
 *
 * Filter-Strategie:
 *   - Nur an gleicher-Origin-API-Pfade anhängen (`/api/...`-Prefix oder
 *     der Pilot-Domain `admin.fatip.ch/api/...`). Externe URLs (z.B.
 *     `kc.fatip.ch/.../userinfo` an Keycloak selbst) bleiben unangetastet —
 *     dort hat angular-oauth2-oidc seinen eigenen Auth-Pfad.
 *   - Wenn kein Token verfügbar (= User nicht eingeloggt), wird kein
 *     Header gesetzt. Backend antwortet dann mit anonymem Principal —
 *     für public-Endpoints (z.B. /api/v1/oidc-config, /api/v1/healthz)
 *     ist das das gewünschte Verhalten.
 *
 * Token-Lifecycle: angular-oauth2-oidc kümmert sich um Refresh via
 * `setupAutomaticSilentRefresh()` (siehe FatipOidcAuthService:359).
 * Bei expired Tokens läuft die Refresh-Mechanik im Hintergrund. Wenn
 * der Refresh fehlschlägt (z.B. Refresh-Token expired), liefert
 * `getAccessToken()` einen leeren String — wir senden dann keinen
 * Header, Backend antwortet anonym, FE-Auth-Guard redirected zum
 * Login.
 *
 * Sicherheit:
 *   - Bearer-Token darf NICHT in `Referer` leaken — gleicher Origin,
 *     daher unkritisch
 *   - Token wird in localStorage gespeichert (angular-oauth2-oidc-
 *     Default) — XSS-Risiko, aber das ist eine separate Architektur-
 *     Diskussion (Cookie-basiert vs Token-in-Storage)
 */
export const oidcBearerInterceptor: HttpInterceptorFn = (req, next) => {
  const oauth = inject(OAuthService);

  // Codex-Plan-Pass-Hardening (2026-05-05):
  //   1. hasValidAccessToken() guard — sendet keinen abgelaufenen Token,
  //      verhindert vermeidbare 401-Bursts bei laufendem silent-refresh.
  //   2. isSameOriginApiRequest() — robust gegen URL-Spoofing wie
  //      `something.api.example.com` (alter `includes('/api/')`-Check
  //      hätte das gematched).
  if (!oauth.hasValidAccessToken() || !isSameOriginApiRequest(req)) {
    return next(req);
  }

  const token = oauth.getAccessToken();
  if (!token) {
    return next(req);
  }

  return next(req.clone({
    setHeaders: { Authorization: `Bearer ${token}` },
  }));
};

/**
 * Origin-aware Check für „API-Call zum eigenen Backend".
 *
 * Parst die Request-URL gegen `location.origin` und matcht nur wenn:
 *   - Der resolvierte Origin == aktueller Browser-Origin (= same-origin)
 *   - UND der Pathname mit `/api/` startet
 *
 * Damit fallen Externe Hosts (kc.fatip.ch, Drittanbieter,
 * `something.api.example.com`) automatisch raus — Token-Leak-Schutz.
 *
 * Ausnahme: leere oder relative URLs (`/api/v1/tasks`) werden via
 * `new URL(url, base)` zu absoluten URLs aufgelöst — relative-Pfade
 * landen damit immer in same-origin.
 */
function isSameOriginApiRequest(req: HttpRequest<unknown>): boolean {
  let parsed: URL;
  try {
    parsed = new URL(req.url, location.origin);
  } catch {
    // Malformed URL — defensive: kein Bearer
    return false;
  }
  if (parsed.origin !== location.origin) return false;
  return parsed.pathname.startsWith('/api/');
}
